import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, ArrowRight, Clock, Users, Target, TrendingUp, Zap, Shield, Award, Star, Sparkles } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Chatbot from '../components/Chatbot';
import { scrollTo, scrollToSection } from '../utils/smoothScroll';

const servicesData = {
  'strategic-advisory': {
    id: 'strategic-advisory',
    title: 'Strategic Advisory',
    subtitle: 'Il tuo partner strategico di fiducia',
    heroText: 'Smetti di prendere decisioni al buio. Avere un consulente strategico al tuo fianco significa avere chiarezza, direzione e risultati.',
    price: 'Da €3.000/mese',
    duration: 'Collaborazione continuativa',
    icon: 'Crown',
    color: '#d4883a',
    problem: {
      title: 'Ti riconosci in questa situazione?',
      points: [
        'Prendi decisioni importanti senza un confronto strategico di alto livello',
        'Il tuo team esegue, ma manca una visione chiara di lungo termine',
        'Ogni mese investi in marketing senza sapere se stai andando nella direzione giusta',
        'Ti senti solo nelle scelte che determineranno il futuro della tua azienda',
      ]
    },
    solution: {
      title: 'Come funziona',
      description: 'Divento il tuo partner strategico. Non un consulente che sparisce dopo un progetto, ma una figura presente, coinvolta, dedicata al successo della tua azienda.',
      steps: [
        {
          title: 'Call settimanali dedicate',
          description: 'Un\'ora alla settimana dedicata esclusivamente a te e alle tue sfide strategiche.',
          icon: Clock
        },
        {
          title: 'Accesso diretto prioritario',
          description: 'Disponibile via WhatsApp o email con risposta garantita entro 24 ore.',
          icon: Zap
        },
        {
          title: 'Review trimestrale',
          description: 'Analisi dei risultati, ricalibrazione obiettivi e pianificazione successiva.',
          icon: Target
        },
        {
          title: 'Supporto decisioni critiche',
          description: 'Ogni decisione importante viene valutata insieme con dati e strategia.',
          icon: Shield
        }
      ]
    },
    benefits: [
      'Chiarezza strategica su ogni decisione importante',
      'Un alleato esperto sempre al tuo fianco',
      'Riduzione dello stress decisionale',
      'Accelerazione verso i tuoi obiettivi di business',
      'Accesso a 14+ anni di esperienza nel marketing strategico',
      'Confronto strategico continuo e personalizzato',
    ],
    ideal: {
      title: 'Perfetto per te se...',
      points: [
        'Sei CEO o Founder e vuoi un confronto strategico di alto livello',
        'Fatturi oltre €100K e vuoi scalare con metodo',
        'Sai che il marketing è un investimento, non una spesa',
        'Sei pronto a passare dal "fare" al "dominare"',
      ]
    },
    notFor: [
      'Chi cerca soluzioni rapide e miracolose',
      'Startup in fase pre-revenue',
      'Chi non è disposto a investire nel lungo termine',
      'Chi preferisce fare tutto da solo senza confronto',
    ],
    testimonial: {
      text: 'Avere Nicola come advisor ha cambiato il modo in cui prendo decisioni. Ogni scelta ora è supportata da dati e strategia. Il fatturato è cresciuto del 180% in 18 mesi.',
      author: 'Marco R.',
      role: 'CEO, Azienda Tech B2B',
      result: '+180% fatturato'
    },
    cta: 'Richiedi una Call Conoscitiva'
  },
  'piano-marketing': {
    id: 'piano-marketing',
    title: 'Piano Marketing Strategico',
    subtitle: 'Il blueprint per dominare il tuo mercato',
    heroText: 'Non un altro documento che finisce in un cassetto. Un piano d\'azione concreto, misurabile, costruito per portarti risultati.',
    price: 'Da €8.000',
    duration: 'Progetto 6-8 settimane',
    icon: 'Target',
    color: '#0b114e',
    problem: {
      title: 'Questi problemi ti suonano familiari?',
      points: [
        'Investi in marketing ma non sai cosa funziona davvero',
        'La tua comunicazione è uguale a quella dei competitor',
        'Non hai un posizionamento chiaro che ti differenzi',
        'Ogni agenzia ti propone tattiche diverse senza una visione d\'insieme',
      ]
    },
    solution: {
      title: 'Cosa include',
      description: 'Un documento operativo di 50+ pagine che diventa la tua bussola per i prossimi 12 mesi. Non teoria, ma azioni concrete.',
      steps: [
        {
          title: 'Analisi approfondita',
          description: 'Studio del tuo mercato, competitor, clienti ideali e interviste strategiche.',
          icon: Target
        },
        {
          title: 'Posizionamento strategico',
          description: 'Definiamo come vuoi essere percepito e il tuo spazio unico nel mercato.',
          icon: Star
        },
        {
          title: 'Strategia go-to-market',
          description: 'Piano dettagliato di canali, messaggi, budget con KPI misurabili.',
          icon: TrendingUp
        },
        {
          title: 'Piano operativo 12 mesi',
          description: 'Timeline con priorità, risorse necessarie e milestone trimestrali.',
          icon: Clock
        }
      ]
    },
    benefits: [
      'Chiarezza totale su dove investire ogni euro',
      'Posizionamento differenziante rispetto ai competitor',
      'KPI chiari per misurare i progressi',
      'Allineamento di tutto il team sulla strategia',
      'Roadmap concreta per i prossimi 12 mesi',
      'Documento operativo da consultare nel tempo',
    ],
    ideal: {
      title: 'Perfetto per te se...',
      points: [
        'Fatturi €200K+ e vuoi strutturare il marketing',
        'Sei stanco di navigare a vista',
        'Hai un team marketing che ha bisogno di direzione',
        'Stai affrontando un rilancio o riposizionamento',
      ]
    },
    notFor: [
      'Chi cerca solo esecuzione tattica',
      'Aziende senza budget per implementare il piano',
      'Chi non ha tempo da dedicare alle interviste iniziali',
      'Chi vuole risultati senza una strategia chiara',
    ],
    testimonial: {
      text: 'Il piano di Nicola ci ha dato la chiarezza che cercavamo da anni. Finalmente sappiamo esattamente dove stiamo andando e come arrivarci.',
      author: 'Laura B.',
      role: 'Founder, E-commerce Fashion',
      result: 'ROI 12x'
    },
    cta: 'Richiedi il Tuo Piano Strategico'
  },
  'acceleratore-crescita': {
    id: 'acceleratore-crescita',
    title: 'Acceleratore di Crescita',
    subtitle: 'Programma intensivo 90 giorni',
    heroText: '90 giorni per trasformare il tuo marketing. Non consulenza passiva, ma lavoro fianco a fianco per implementare strategie di crescita aggressive.',
    price: 'Da €15.000',
    duration: 'Programma 90 giorni',
    icon: 'Rocket',
    color: '#d4883a',
    problem: {
      title: 'Sai cosa devi fare, ma non riesci a farlo?',
      points: [
        'Hai già una strategia ma l\'implementazione è lenta',
        'Il tuo team è sovraccarico e il marketing resta indietro',
        'Vuoi risultati veloci ma non sai da dove partire',
        'Hai provato diverse agenzie ma i risultati non arrivano',
      ]
    },
    solution: {
      title: 'Il percorso',
      description: 'Non ti lascio solo con un documento. Lavoriamo insieme, ogni settimana, per 90 giorni intensivi di trasformazione.',
      steps: [
        {
          title: 'Settimana 1-2: Diagnosi',
          description: 'Analizziamo dati, processi, team e asset. Identifichiamo le leve di crescita.',
          icon: Target
        },
        {
          title: 'Settimana 3-6: Quick wins',
          description: 'Implementiamo azioni ad alto impatto per risultati già nel primo mese.',
          icon: Zap
        },
        {
          title: 'Settimana 7-10: Sistema',
          description: 'Costruiamo processi e sistemi per una crescita sostenibile.',
          icon: TrendingUp
        },
        {
          title: 'Settimana 11-12: Autonomia',
          description: 'Formiamo il team, documentiamo tutto, prepariamo il passaggio.',
          icon: Users
        }
      ]
    },
    benefits: [
      'Risultati tangibili già nelle prime settimane',
      'Implementazione garantita, non solo strategia',
      'Team interno formato e autonomo a fine percorso',
      'Processi replicabili per continuare a crescere',
      'Supporto intensivo per 90 giorni',
      'Metriche di crescita misurabili e condivise',
    ],
    ideal: {
      title: 'Perfetto per te se...',
      points: [
        'Sei pronto a scalare rapidamente',
        'Hai budget e risorse per implementare',
        'Il tuo team vuole imparare facendo',
        'Fatturi €100K+ e punti a crescere',
      ]
    },
    notFor: [
      'Chi cerca risultati senza impegno',
      'Aziende senza un team minimo per eseguire',
      'Chi non può dedicare tempo al programma',
      'Chi non è pronto a mettere in discussione i processi attuali',
    ],
    testimonial: {
      text: 'In 90 giorni abbiamo fatto più progressi che nei 2 anni precedenti. Il programma è intenso ma i risultati parlano: +65% di lead qualificati.',
      author: 'Alessandro M.',
      role: 'Direttore Marketing, Gruppo Industriale',
      result: '+65% lead'
    },
    cta: 'Candidati all\'Acceleratore'
  },
  'audit-diagnosi': {
    id: 'audit-diagnosi',
    title: 'Audit & Diagnosi',
    subtitle: 'Scopri cosa non funziona (e cosa sì)',
    heroText: 'Prima di investire un altro euro in marketing, scopri esattamente dove stai perdendo soldi e dove invece hai opportunità nascoste.',
    price: 'Da €2.500',
    duration: 'Analisi 2-3 settimane',
    icon: 'LineChart',
    color: '#0b114e',
    problem: {
      title: 'Hai bisogno di chiarezza?',
      points: [
        'Non sai se il tuo marketing sta funzionando davvero',
        'Sospetti di sprecare budget ma non sai dove',
        'Vorresti capire cosa fanno meglio i competitor',
        'Hai bisogno di una valutazione obiettiva esterna',
      ]
    },
    solution: {
      title: 'Cosa analizziamo',
      description: 'Un\'analisi completa e impietosa del tuo marketing. Niente complimenti di circostanza, solo verità e azioni concrete.',
      steps: [
        {
          title: 'Audit dei canali',
          description: 'Performance di ogni canale: paid, organic, email, social. Dove sprechi? Dove investire?',
          icon: TrendingUp
        },
        {
          title: 'Analisi del funnel',
          description: 'Mappiamo il percorso cliente. Dove perdi le persone? Dove i colli di bottiglia?',
          icon: Target
        },
        {
          title: 'Benchmark competitivo',
          description: 'Come ti posizioni rispetto ai competitor? Cosa fanno meglio? Dove puoi batterli?',
          icon: Users
        },
        {
          title: 'Report prioritizzato',
          description: 'Documento con problemi, opportunità scoperte e action plan prioritizzato.',
          icon: CheckCircle
        }
      ]
    },
    benefits: [
      'Visione chiara e oggettiva del tuo marketing',
      'Identificazione immediata degli sprechi',
      'Scoperta di opportunità nascoste',
      'Action plan prioritizzato per intervenire subito',
      'Base solida per decisioni future',
      'Report dettagliato da condividere con il team',
    ],
    ideal: {
      title: 'Perfetto per te se...',
      points: [
        'Vuoi una valutazione esterna imparziale',
        'Stai pianificando un grande investimento marketing',
        'Senti di poter fare meglio ma non sai dove',
        'Stai valutando un cambio di agenzia',
      ]
    },
    notFor: [
      'Chi non è pronto a sentire verità scomode',
      'Aziende senza dati storici da analizzare',
      'Chi cerca solo conferme, non miglioramenti',
      'Chi non vuole investire tempo nell\'analisi iniziale',
    ],
    testimonial: {
      text: 'L\'audit ha rivelato che stavamo sprecando il 40% del budget ads. In un mese abbiamo riallocato e i risultati sono migliorati del 60%.',
      author: 'Francesca D.',
      role: 'Marketing Manager, SaaS B2B',
      result: '-40% sprechi'
    },
    cta: 'Richiedi il Tuo Audit'
  }
};

const ServicePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);

  // Scroll to top on mount
  useEffect(() => {
    scrollTo(0, { immediate: true });
  }, []);

  useEffect(() => {
    if (slug && servicesData[slug]) {
      setService(servicesData[slug]);
    } else {
      navigate('/');
    }
  }, [slug, navigate]);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      scrollToSection('contact');
    }, 100);
  };

  if (!service) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#d4883a] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Navbar />
      
      {/* Hero */}
      <section className="bg-[#0b114e] pt-32 pb-20 sm:pt-40 sm:pb-28 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#d4883a]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 relative">
          <Link 
            to="/#services"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Tutti i servizi
          </Link>
          
          <div className="mt-8">
            <p className="text-[#d4883a] font-semibold mb-4">{service.duration}</p>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              {service.title}
            </h1>
            <p className="text-xl sm:text-2xl text-white/80 mb-8 leading-relaxed">
              {service.heroText}
            </p>
            <div className="flex flex-wrap items-center gap-6">
              <button
                onClick={scrollToContact}
                className="bg-[#d4883a] hover:bg-[#d4896a] text-white font-semibold px-8 py-4 rounded-full transition-all flex items-center gap-2 text-lg"
              >
                {service.cta}
                <ArrowRight className="w-5 h-5" />
              </button>
              <span className="text-white text-xl font-bold">{service.price}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section - Premium */}
      <section className="py-20 sm:py-32 bg-white relative overflow-hidden">
        <div className="absolute top-20 right-0 w-72 h-72 bg-gradient-to-br from-red-100/50 to-transparent rounded-full blur-3xl" />
        
        <div className="max-w-[1000px] mx-auto px-4 sm:px-6 relative">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 text-red-500/80 text-sm font-medium tracking-wider mb-4">
              <span className="w-8 h-px bg-red-300"></span>
              Il problema
              <span className="w-8 h-px bg-red-300"></span>
            </span>
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0b114e]"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              {service.problem.title}
            </h2>
          </div>
          
          <div className="flex flex-col gap-6">
            {service.problem.points.map((point, index) => (
              <div 
                key={index}
                className="group flex items-start gap-5 p-7 bg-gradient-to-r from-gray-50 to-white rounded-2xl border border-gray-100 hover:border-red-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 group-hover:bg-red-200 transition-colors">
                  <span className="text-red-500 font-bold">{index + 1}</span>
                </div>
                <p className="text-gray-700 text-lg leading-relaxed pt-1.5">{point}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-16 text-center">
            <p className="text-xl text-[#0b114e] font-medium inline-flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-[#d4883a]" />
              Se ti riconosci, questo servizio è stato creato per te.
              <Sparkles className="w-5 h-5 text-[#d4883a]" />
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section - Premium */}
      <section className="py-20 sm:py-32 bg-[#f8fafc] relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #0b114e 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 relative">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 text-[#d4883a] text-sm font-medium tracking-wider mb-4">
              <span className="w-8 h-px bg-[#d4883a]/30"></span>
              La soluzione
              <span className="w-8 h-px bg-[#d4883a]/30"></span>
            </span>
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0b114e] mb-6"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              {service.solution.title}
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
              {service.solution.description}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {service.solution.steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div 
                  key={index}
                  className="group bg-white p-8 lg:p-10 rounded-3xl shadow-sm border border-gray-100 hover:shadow-2xl hover:border-[#d4883a]/20 transition-all duration-500 relative overflow-hidden"
                >
                  {/* Background number */}
                  <span className="absolute -top-4 -right-4 text-8xl font-bold text-gray-50 group-hover:text-[#d4883a]/5 transition-colors select-none">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  
                  <div className="relative">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#d4883a]/10 to-[#d4883a]/5 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-7 h-7 text-[#d4883a]" />
                    </div>
                    <h3 className="text-xl lg:text-2xl font-bold text-[#0b114e] mb-3">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section - Premium */}
      <section className="py-20 sm:py-32 bg-[#0b114e] relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#d4883a]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        
        <div className="max-w-[1000px] mx-auto px-4 sm:px-6 relative">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 text-[#d4883a] text-sm font-medium tracking-wider mb-4">
              <span className="w-8 h-px bg-[#d4883a]/50"></span>
              I benefici
              <span className="w-8 h-px bg-[#d4883a]/50"></span>
            </span>
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Cosa otterrai
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {service.benefits.map((benefit, index) => (
              <div 
                key={index}
                className="group flex items-start gap-4 p-6 h-[130px] bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 hover:border-[#d4883a]/30 transition-all duration-300"
              >
                <div className="w-8 h-8 rounded-full bg-[#d4883a]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#d4883a]/30 transition-colors">
                  <CheckCircle className="w-4 h-4 text-[#d4883a]" />
                </div>
                <p className="text-white/90 leading-relaxed">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal Client Section - Premium */}
      <section className="py-20 sm:py-32 bg-gradient-to-b from-[#f8fafc] to-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#d4883a]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-[#0b114e]/5 rounded-full blur-3xl" />
        
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 relative">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 text-[#d4883a] text-sm font-medium tracking-wider mb-4">
              <span className="w-8 h-px bg-[#d4883a]/30"></span>
              A chi è rivolto
              <span className="w-8 h-px bg-[#d4883a]/30"></span>
            </span>
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0b114e]"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              È il servizio giusto per te?
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* For - Premium Card */}
            <div className="group relative">
              {/* Glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-br from-[#d4883a]/20 to-[#0b114e]/20 rounded-[2rem] blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative h-full bg-white p-8 lg:p-10 rounded-[2rem] border border-gray-100 shadow-lg hover:shadow-2xl transition-all duration-500">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-100">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#d4883a] to-[#d4896a] rounded-2xl flex items-center justify-center shadow-lg shadow-[#d4883a]/20">
                    <CheckCircle className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 
                      className="text-2xl font-bold text-[#0b114e]"
                      style={{ fontFamily: 'Georgia, serif' }}
                    >
                      {service.ideal.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">Il profilo ideale</p>
                  </div>
                </div>
                
                {/* Points */}
                <div className="flex flex-col gap-6">
                  {service.ideal.points.map((point, index) => (
                    <div key={index} className="flex items-start gap-4 group/item min-h-[60px]">
                      <span className="w-8 h-8 rounded-xl bg-[#d4883a]/10 flex items-center justify-center flex-shrink-0 group-hover/item:bg-[#d4883a]/20 transition-colors">
                        <CheckCircle className="w-4 h-4 text-[#d4883a]" />
                      </span>
                      <p className="text-gray-700 leading-relaxed pt-1">{point}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Not For - Premium Card */}
            <div className="group relative">
              {/* Subtle glow */}
              <div className="absolute -inset-1 bg-gradient-to-br from-gray-200/50 to-gray-300/30 rounded-[2rem] blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative h-full bg-white p-8 lg:p-10 rounded-[2rem] border border-gray-100 shadow-lg hover:shadow-2xl transition-all duration-500">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-100">
                  <div className="w-14 h-14 bg-gradient-to-br from-gray-400 to-gray-500 rounded-2xl flex items-center justify-center shadow-lg shadow-gray-400/20">
                    <Shield className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 
                      className="text-2xl font-bold text-[#0b114e]"
                      style={{ fontFamily: 'Georgia, serif' }}
                    >
                      Non è per te se...
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">Per trasparenza</p>
                  </div>
                </div>
                
                {/* Points */}
                <div className="flex flex-col gap-6">
                  {service.notFor.map((point, index) => (
                    <div key={index} className="flex items-start gap-4 group/item min-h-[60px]">
                      <span className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover/item:bg-gray-200 transition-colors">
                        <span className="text-gray-400 font-medium">✕</span>
                      </span>
                      <p className="text-gray-500 leading-relaxed pt-1">{point}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial - Premium */}
      <section className="py-20 sm:py-32 bg-[#f8fafc] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white to-transparent opacity-50" />
        
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 relative">
          <div className="bg-white p-10 sm:p-14 rounded-[2rem] shadow-xl border border-gray-100 relative overflow-hidden">
            {/* Decorative quote */}
            <div className="absolute top-8 left-8 text-[120px] font-serif text-gray-100 leading-none select-none">"</div>
            
            <div className="relative">
              {/* Result badge */}
              <div className="flex justify-center mb-8">
                <span className="inline-flex items-center gap-2 bg-[#d4883a]/10 text-[#d4883a] font-bold px-5 py-2 rounded-full">
                  <TrendingUp className="w-4 h-4" />
                  {service.testimonial.result}
                </span>
              </div>
              
              <blockquote 
                className="text-xl sm:text-2xl lg:text-3xl text-[#0b114e] font-medium mb-10 leading-relaxed text-center"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                "{service.testimonial.text}"
              </blockquote>
              
              <div className="flex items-center justify-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-[#0b114e] to-[#2d4a6f] rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {service.testimonial.author.charAt(0)}
                </div>
                <div className="text-left">
                  <p className="font-bold text-[#0b114e] text-lg">{service.testimonial.author}</p>
                  <p className="text-gray-500">{service.testimonial.role}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Premium */}
      <section className="py-20 sm:py-32 bg-gradient-to-br from-[#0b114e] to-[#152a42] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#d4883a]/30 to-transparent" />
        <div className="absolute top-1/4 right-0 w-72 h-72 bg-[#d4883a]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
        
        <div className="max-w-[700px] mx-auto px-4 sm:px-6 text-center relative">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-5 py-2 mb-8">
            <span className="w-2 h-2 bg-[#d4883a] rounded-full animate-pulse" />
            <span className="text-white/70 text-sm">Posti limitati ogni mese</span>
          </div>
          
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Pronto a iniziare?
          </h2>
          <p className="text-white/70 text-lg sm:text-xl mb-12 leading-relaxed">
            Il primo passo è una call conoscitiva gratuita. Parliamo del tuo business, dei tuoi obiettivi, e valutiamo insieme se questo percorso è adatto a te.
          </p>
          
          <button
            onClick={scrollToContact}
            className="group bg-[#d4883a] hover:bg-[#d4896a] text-white font-bold px-10 sm:px-12 py-5 sm:py-6 rounded-full text-lg sm:text-xl transition-all inline-flex items-center gap-3 hover:scale-105 shadow-lg shadow-[#d4883a]/25"
          >
            {service.cta}
            <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </button>
          
          <p className="text-white/40 text-sm mt-8">
            Rispondo personalmente entro 48 ore
          </p>
        </div>
      </section>

      <Footer />
      <Chatbot />
    </div>
  );
};

export default ServicePage;
